"use strict";

/**
 * DGF-3054 — DOM plumbing shared by the express surfaces. Injected `doc` throughout so it can be
 * unit tested under jest's node environment.
 */

const HIDDEN_CLASS = "d-none";

/**
 * Collapses a whole express section. `_expressPayments.scss:127-131` makes this `display: none` on
 * the SECTION, so everything inside it goes with it — the divider, the buttons AND the error
 * element. That total reach is why setSurfaceError and setSurfaceVisibility below have to agree
 * about it rather than each toggling it on its own.
 */
const EMPTY_CLASS = "express-section--empty";

/**
 * The PDP add-to-bag button, whose rendered width the wallets have to match.
 *
 * app_superdry/.../product/components/addToCartProduct.isml puts it in a d-flex wrapper alongside
 * the wishlist icon, so although .add-to-cart is width:100% (product/detail.scss:715) its rendered
 * width is the column minus the wishlist icon. That icon's width is set in product/detail.scss and
 * would have to be kept in step here as a CSS constant — hence measuring the button instead of
 * hard-coding an offset.
 */
const ADD_TO_BAG_SELECTOR = ".product-add-to-cart-wrapper .add-to-cart";

/** Custom property the stylesheet reads for the PDP wallet width. Falls back to 100%. */
const WIDTH_PROPERTY = "--sd-express-width";

/**
 * Gives the express CSRF token element the id the Adyen client expects, without ever creating a
 * duplicate id.
 *
 * app_adyen_SFRA/.../js/adyen/commons/httpClient.js:11-16 injects
 * csrf_token: $('#adyen-token').val() into every express request, so exactly one element must
 * carry that id. Each surface renders its token with a class rather than an id, because a cart
 * page with the mini bag open renders two of them and any one valid session token satisfies every
 * express endpoint.
 *
 * @param {Object} doc - document-like object
 * @returns {boolean} whether a usable token element is present
 */
function promoteTokenElement(doc) {
    if (!doc) {
        return false;
    }
    if (doc.getElementById("adyen-token")) {
        return true;
    }
    const candidate = doc.querySelector(".js-adyen-express-token");
    if (!candidate) {
        return false;
    }
    candidate.setAttribute("id", "adyen-token");
    return true;
}

/**
 * Finds the express containers currently in the document.
 *
 * @param {Object} doc - document-like object
 * @param {string} [surface] - restrict to one surface
 * @returns {Object[]} matching container elements
 */
function findExpressContainers(doc, surface) {
    if (!doc || typeof doc.querySelectorAll !== "function") {
        return [];
    }
    const selector = surface
        ? `.js-adyen-express[data-express-surface="${surface}"]`
        : ".js-adyen-express";
    return Array.prototype.slice.call(doc.querySelectorAll(selector));
}

/**
 * Whether the surface currently has something for the shopper to read.
 *
 * The error element's textContent is the source of truth rather than its d-none class, because
 * textContent is what setSurfaceError writes and clears; the class is only how it is hidden.
 *
 * @param {Object} section - the express section element (the container's parent)
 * @returns {boolean} whether a non-empty error message is on the surface
 */
function hasSurfaceMessage(section) {
    if (!section || typeof section.querySelector !== "function") {
        return false;
    }
    const errorElement = section.querySelector(".js-adyen-express-error");
    return Boolean(errorElement && errorElement.textContent);
}

/**
 * Shows or hides the decoration around a container — the "OR" divider on the cart page — so a
 * surface with no rendered wallet shows no orphaned divider or empty gap.
 *
 * A SECTION CARRYING AN ERROR MESSAGE IS NOT EMPTY, so `hasWallets === false` does not collapse it
 * in that case. Without that distinction the bag's "could not start" copy was written and then
 * hidden in the same pass: `cartWallets.js:297` calls setSurfaceError and `:298` immediately calls
 * setSurfaceVisibility(container, false), whose EMPTY_CLASS is `display: none` on the whole section,
 * error element included (`_expressPayments.scss:127-131`). Reported on PR #1979 as an unreachable
 * message, and the stale "KNOWN LIMITATION" note at `cartExpressPayments.js:71-78` describes that
 * old state — the reorder it asks for in cartWallets.js is no longer needed.
 *
 * Fixed here rather than by reordering the two calls, because the collapse must lose to a message
 * whatever order the caller happens to use, and because this file is the one shared by all three
 * surfaces (bag, mini bag, PDP) — a caller-side reorder would have to be repeated in each and could
 * be forgotten in the next one.
 *
 * The silent collapse itself is deliberate and is kept: "no wallets available in your region" is a
 * legitimate state with nothing to say, and the section has to collapse so the bag shows no empty
 * gap above the checkout button. Only the presence of a real message overrides it, so an empty
 * section with no message can never be resurrected — see the test of exactly that.
 *
 * @param {Object} container - the express container element
 * @param {boolean} hasWallets - whether any wallet mounted
 * @returns {void}
 */
function setSurfaceVisibility(container, hasWallets) {
    if (!container || !container.parentNode) {
        return;
    }
    const section = container.parentNode;
    const divider = typeof section.querySelector === "function"
        ? section.querySelector(".js-adyen-express-divider")
        : null;

    if (divider && divider.classList) {
        // The divider stays hidden even when an error is shown: it is the "OR" separator between the
        // wallets and the checkout button, and there are no wallets to separate.
        if (hasWallets) {
            divider.classList.remove(HIDDEN_CLASS);
        } else {
            divider.classList.add(HIDDEN_CLASS);
        }
    }

    if (section.classList) {
        if (hasWallets || hasSurfaceMessage(section)) {
            section.classList.remove(EMPTY_CLASS);
        } else {
            section.classList.add(EMPTY_CLASS);
        }
    }
}

/**
 * Shows or clears the "confirming your order" state.
 *
 * Set once a wallet has authorised and the confirmation form is posting: until this existed the
 * shopper saw nothing between the wallet sheet closing and the redirect, so a completed payment
 * looked like a button that did nothing.
 *
 * Also blocks pointer events on the buttons while it is set — see the stylesheet. A wallet that has
 * already authorised must not be re-openable, because a second authorisation would be a second
 * charge, and the confirmation POST is a native form submit that cannot be cancelled once away.
 *
 * @param {Object} container - the express container element
 * @param {boolean} isBusy - whether the surface is confirming
 * @returns {void}
 */
function setSurfaceBusy(container, isBusy) {
    if (!container || !container.parentNode || typeof container.parentNode.querySelector !== "function") {
        return;
    }
    const section = container.parentNode;
    const busyElement = section.querySelector(".js-adyen-express-busy");
    if (busyElement && busyElement.classList) {
        if (isBusy) {
            busyElement.classList.remove(HIDDEN_CLASS);
        } else {
            busyElement.classList.add(HIDDEN_CLASS);
        }
    }
    if (section.classList) {
        if (isBusy) {
            section.classList.add("express-section--busy");
        } else {
            section.classList.remove("express-section--busy");
        }
    }
    if (typeof section.setAttribute === "function") {
        section.setAttribute("aria-busy", isBusy ? "true" : "false");
    }
}

/**
 * Whether an element is actually rendered, i.e. not inside a `display: none` subtree.
 *
 * REVIEW FINDING — the MINI BAG needs this. It has its own error element (the shared non-sticky
 * branch of cart/expressPayments.isml emits one for both the `cart` and `minicart` surfaces), so it
 * never reached the page-level fallback below — but that element lives inside `.minicart .popover`,
 * which is `display: none` unless `.show` is set (`_miniCart.scss`), and `miniCart.js` strips
 * `.show` on an outside click AND on `mouseleave focusout`. Opening the mini bag and clicking PayPal
 * hands focus to the SDK popup, which fires exactly that — so the popover closes, and a failure
 * message was written into a hidden subtree. Silent, on a launch surface, same shape as the
 * sticky-bar bug.
 *
 * `offsetParent` is the standard test: it is null for any element in a `display: none` subtree.
 * `undefined` means we cannot tell — the jest DOM double does not implement it — so that is treated
 * as visible rather than guessing, which keeps behaviour unchanged everywhere it is not implemented.
 *
 * @param {Object} element - the candidate error element
 * @returns {boolean} whether it can actually be seen
 */
function isRendered(element) {
    return !(element && element.offsetParent === null);
}

/**
 * The IN-PAGE BAG's error element, for a surface that has none of its own (the sticky bar).
 *
 * Walks up to the root rather than taking a document from the caller, because setSurfaceError is
 * given only a container — and walking works identically for a real DOM (ending at the document
 * node) and for the test double.
 *
 * REVIEW FINDING — this must not simply take the FIRST match in document order, which is what it
 * did. `cart/expressPayments.isml`'s non-sticky branch emits the error element for BOTH the `cart`
 * and `minicart` surfaces, and the mini bag lives in the header, EARLIER in the DOM than the in-page
 * bag block. So once the shopper had opened the mini bag — which injects that fragment and leaves it
 * in the DOM — a sticky-bar failure wrote its message into the mini bag's element, inside a
 * collapsed header panel nobody can see, and un-collapsed the HEADER's section instead of the
 * visible one. Silent again, plus a stray class change in the header. It only looked right on a
 * fresh cart load, before the mini-bag fragment exists.
 *
 * So the target is chosen by SURFACE rather than by position: the `cart` block is the one the
 * template means by "one message, in the block that was designed to hold it", and it is on the same
 * page as the bar by construction (the bar is a second include on the cart page). The last match is
 * kept only as a defensive fallback — on a cart page it is that same in-page block, since the bar
 * itself carries no element.
 *
 * @param {Object} section - the express section whose own error element is missing
 * @returns {Object|null} the in-page bag's error element, or null when the page has none
 */
function findPageErrorElement(section) {
    let root = section;

    while (root.parentNode) {
        root = root.parentNode;
    }

    if (typeof root.querySelectorAll !== "function") {
        return null;
    }

    const candidates = Array.prototype.slice.call(root.querySelectorAll(".js-adyen-express-error"));

    if (!candidates.length) {
        return null;
    }

    // A hidden candidate is no better than no candidate — see isRendered.
    const visible = candidates.filter(isRendered);
    const usable = visible.length > 0 ? visible : candidates;

    const inPageBag = usable.filter(function belongsToBag(errorElement) {
        const host = errorElement.parentNode;
        const container = host && typeof host.querySelector === "function"
            ? host.querySelector(".js-adyen-express")
            : null;
        return Boolean(container && typeof container.getAttribute === "function"
            && container.getAttribute("data-express-surface") === "cart");
    })[0];

    return inPageBag || usable[usable.length - 1];
}

/**
 * Surfaces an error next to the wallets. Express failures are otherwise silent — the vendor logs
 * to the console and leaves the shopper looking at an empty box.
 *
 * Clears the confirming state whenever a message is shown, so the two can never be on screen at
 * once. That pairing is the whole failure mode this guards against: something goes wrong after
 * authorisation, the error appears, and a spinner keeps turning underneath it forever.
 *
 * Showing a message also UN-COLLAPSES the section, because a previous pass may already have hidden
 * it: `cartWallets.js:296-299` collapses every container on a failed payment-methods request, and the
 * PDP's own failure path (`productWallets.js:506`) writes into a section the server may have rendered
 * with no wallets at all. An error element inside a `display: none` section is not an error message.
 *
 * Clearing a message deliberately does NOT re-collapse. Whether an empty section should be hidden
 * depends on whether any wallet mounted, which only the caller knows, so that decision stays in
 * setSurfaceVisibility — and `cartWallets.js:138` clears the error at the START of a pass, before it
 * knows the answer. Re-collapsing here would hide the wallets that pass is about to mount.
 *
 * @param {Object} container - the express container element
 * @param {string} message - message to display, empty string clears it
 * @returns {void}
 */
function setSurfaceError(container, message) {
    if (message) {
        setSurfaceBusy(container, false);
    }

    if (!container || !container.parentNode || typeof container.parentNode.querySelector !== "function") {
        return;
    }
    const section = container.parentNode;
    const ownErrorElement = section.querySelector(".js-adyen-express-error");

    /*
     * REVIEW FINDING — a surface with no error element of its own must not fail silently.
     *
     * The mobile sticky bar deliberately carries no error element (see cart/expressPayments.isml for
     * why: a strip pinned over the bag repeating the message the in-page block already shows, eight
     * lines down the same page). That reasoning holds for the /paymentMethods failure, which
     * cartWallets' catch applies to EVERY container — the in-page block gets the message either
     * way. It does not hold for a failure that belongs to one container: completeOrder's catch and
     * the SDK's onError write to the container that failed and nowhere else, so a failed return or
     * placement from the BAR showed the shopper nothing, anywhere — the spinner stopped, the bar
     * came back, and no message appeared. With PayPal in the bar (the Germany configuration) that
     * is the whole express payment path failing in silence.
     *
     * So the message is routed to the page's own error element instead of being dropped. That is
     * the template's stated intent — "one message, in the block that was designed to hold it" —
     * rather than a departure from it.
     *
     * Only when SETTING a message. A clearing pass must never reach across sections: cartWallets
     * clears every container at the start of a pass, so a bar clearing the in-page block's message
     * would wipe one that pass is about to re-establish.
     */
    /*
     * An own element that is NOT RENDERED counts as not having one — see isRendered. That is the
     * mini bag: its popover closes the moment the PayPal popup takes focus, so its own element is
     * inside a display:none subtree by the time a failure is reported.
     */
    const ownUsable = ownErrorElement && isRendered(ownErrorElement);
    const errorElement = ownUsable
        ? ownErrorElement
        : (message ? findPageErrorElement(section) : ownErrorElement);

    if (!errorElement) {
        return;
    }

    /*
     * Un-collapse the section that will actually SHOW the message, which is not always the one that
     * failed. Previously this always un-collapsed the failing section, so a sticky-bar failure
     * revealed the bar and then displayed nothing in it.
     */
    const messageSection = ownUsable ? section : errorElement.parentNode;

    if (message && messageSection && messageSection.classList) {
        messageSection.classList.remove(EMPTY_CLASS);
    }
    errorElement.textContent = message || "";
    if (errorElement.classList) {
        if (message) {
            errorElement.classList.remove(HIDDEN_CLASS);
        } else {
            errorElement.classList.add(HIDDEN_CLASS);
        }
    }
}

/**
 * Sizes the PDP wallets to the add-to-bag button, so add-to-bag, Apple Pay and Google Pay read as
 * one consistent column.
 *
 * Measured rather than hard-coded: see ADD_TO_BAG_SELECTOR. The value is published as a custom
 * property on the section and consumed by the grid, so if the measurement is unavailable the
 * stylesheet's 100% fallback applies and the buttons are still equal to each other.
 *
 * Scoped by document order, which is correct in practice: express is PDP-only and the quick view
 * renders no express container, so the first match is the PDP's own button.
 *
 * @param {Object} doc - document-like object
 * @param {Object} container - the express container element
 * @returns {number|null} the measured width in px, or null when it could not be measured
 */
function matchWidthToAddToBag(doc, container) {
    if (!doc || typeof doc.querySelector !== "function" || !container || !container.parentNode) {
        return null;
    }
    const section = container.parentNode;
    if (!section.style || typeof section.style.setProperty !== "function") {
        return null;
    }

    const reference = doc.querySelector(ADD_TO_BAG_SELECTOR);
    const width = reference ? reference.offsetWidth : 0;

    if (!width) {
        // Nothing to match (add-to-bag hidden behind notify-me, or not laid out yet). Drop back to
        // the stylesheet default rather than freezing a stale pixel value.
        if (typeof section.style.removeProperty === "function") {
            section.style.removeProperty(WIDTH_PROPERTY);
        }
        return null;
    }

    section.style.setProperty(WIDTH_PROPERTY, `${width}px`);
    return width;
}

/**
 * Creates (or refreshes) the hidden form the vendor's createTemporaryBasket reads.
 *
 * commons/index.js:51-54 does document.getElementById('express-product-form') and parses the
 * value of #selected-express-product, so both ids have to exist with the selected variant in
 * them. There is exactly one PDP express surface per page, so a single instance is correct.
 *
 * @param {Object} doc - document-like object
 * @param {Object} container - the express container element
 * @param {Object} product - product payload to serialise
 * @returns {Object|null} the form element
 */
function upsertExpressProductForm(doc, container, product) {
    if (!doc || !container || !product) {
        return null;
    }
    let form = doc.getElementById("express-product-form");
    if (!form) {
        form = doc.createElement("form");
        form.setAttribute("id", "express-product-form");
        form.setAttribute("name", "express-product-form");
        const input = doc.createElement("input");
        input.setAttribute("id", "selected-express-product");
        input.setAttribute("name", "selected-express-product");
        input.setAttribute("type", "hidden");
        form.appendChild(input);
        container.appendChild(form);
    }
    const productInput = form.querySelector("#selected-express-product");
    if (productInput) {
        productInput.setAttribute("data-pid", `${product.id}`);
        productInput.setAttribute("data-basketId", "");
        productInput.value = JSON.stringify(product);
    }
    return form;
}

module.exports = {
    HIDDEN_CLASS: HIDDEN_CLASS,
    EMPTY_CLASS: EMPTY_CLASS,
    ADD_TO_BAG_SELECTOR: ADD_TO_BAG_SELECTOR,
    WIDTH_PROPERTY: WIDTH_PROPERTY,
    matchWidthToAddToBag: matchWidthToAddToBag,
    promoteTokenElement: promoteTokenElement,
    findExpressContainers: findExpressContainers,
    setSurfaceVisibility: setSurfaceVisibility,
    setSurfaceError: setSurfaceError,
    setSurfaceBusy: setSurfaceBusy,
    upsertExpressProductForm: upsertExpressProductForm
};
